<p-toast />
<p-confirmDialog />

<div class="coupon-edit-page">
  <!-- Loading Skeleton -->
  @if (loading()) {
    <div class="loading-skeleton">
      <div class="skeleton-form">
        <p-skeleton height="2rem" styleClass="mb-2" />
        <p-skeleton height="10rem" styleClass="mb-2" />
        <p-skeleton height="10rem" styleClass="mb-2" />
        <p-skeleton height="8rem" styleClass="mb-2" />
      </div>
      <div class="skeleton-summary">
        <p-skeleton height="16rem" />
      </div>
    </div>
  } @else {
    <!-- Compact Header with Save Button -->
    <div class="page-header">
      <div class="header-left">
        <button
          pButton
          type="button"
          icon="pi pi-arrow-left"
          class="p-button-text p-button-plain p-button-sm back-btn"
          (click)="goBack()"
          pTooltip="Back to Coupons"
        ></button>
        @if (mode === "edit" && couponId) {
          <span class="coupon-id-badge">#{{ couponId }}</span>
        }
      </div>
      <div class="header-right">
        <!-- Mobile Summary Button -->
        <button
          pButton
          type="button"
          icon="pi pi-info-circle"
          class="p-button-rounded p-button-text p-button-sm mobile-summary-btn"
          (click)="summaryPopover.toggle($event)"
          pTooltip="View Summary"
        ></button>
        <!-- Summary Popover for Mobile -->
        <p-popover #summaryPopover styleClass="summary-popover">
          <ng-template pTemplate="content">
            <div class="popover-summary-container">
              <div class="popover-header">
                <h4 class="popover-title">Coupon Summary</h4>
                <button
                  pButton
                  type="button"
                  icon="pi pi-times"
                  class="p-button-rounded p-button-text p-button-sm"
                  (click)="summaryPopover.hide()"
                ></button>
              </div>
              <ng-container *ngTemplateOutlet="summaryContent" />
            </div>
          </ng-template>
        </p-popover>
        <!-- Save Button in Header -->
        <button
          pButton
          type="button"
          [label]="submitButtonLabel"
          icon="pi pi-save"
          class="p-button-sm save-header-btn"
          [loading]="saving()"
          [disabled]="saving() || codeValidating()"
          (click)="onSubmit()"
        ></button>
      </div>
    </div>

    <!-- Retention Workflow Warning -->
    @if (!canEdit()) {
      <div class="retention-warning">
        <i class="pi pi-exclamation-triangle"></i>
        <span>{{ retentionWorkflowMessage() }}</span>
      </div>
    }

    <!-- Form -->
    <form [formGroup]="couponForm" (ngSubmit)="onSubmit()" class="coupon-form">
      <!-- Two Column Layout -->
      <div class="two-column-layout">
        <!-- Left Column: Accordion Sections -->
        <div class="form-column">
          <div class="form-container">
            <p-accordion [multiple]="true" [value]="['0', '1', '2', '3']">
              <!-- Coupon Details Section -->
              <p-accordionpanel value="0">
                <p-accordionheader>
                  <div class="accordion-header">
                    <i class="pi pi-tag"></i>
                    <span>Coupon Details</span>
                  </div>
                </p-accordionheader>
                <p-accordioncontent>
                  <div class="form-grid" formGroupName="details">
                    <div class="form-field">
                      <label for="name">Internal Name</label>
                      <input
                        pInputText
                        id="name"
                        formControlName="name"
                        placeholder="e.g., 10% Off First Order"
                        class="w-full"
                      />
                      <small class="field-hint"
                        >Not shown to customers (max 255)</small
                      >
                      @if (hasError("details.name")) {
                        <small class="p-error">{{
                          getErrorMessage("details.name")
                        }}</small>
                      }
                    </div>
                    <div class="form-field">
                      <label
                        for="code"
                        class="required"
                        [class.has-error]="
                          hasError('details.code') || codeError()
                        "
                        >Coupon Code</label
                      >
                      <div class="p-inputgroup">
                        <input
                          pInputText
                          id="code"
                          formControlName="code"
                          placeholder="e.g., SAVE10"
                          class="w-full code-input"
                          [class.ng-invalid]="
                            hasError('details.code') || codeError()
                          "
                          (keypress)="couponKeyPress($event)"
                        />
                        @if (codeValidating()) {
                          <span class="p-inputgroup-addon">
                            <i class="pi pi-spin pi-spinner"></i>
                          </span>
                        }
                      </div>
                      <small class="field-hint"
                        >Max 20 chars, letters/numbers/-/_</small
                      >
                      @if (hasError("details.code")) {
                        <small class="p-error">{{
                          getErrorMessage("details.code")
                        }}</small>
                      }
                      @if (codeError()) {
                        <small class="p-error">{{ codeError() }}</small>
                      }
                    </div>
                  </div>
                </p-accordioncontent>
              </p-accordionpanel>

              <!-- Configure Discount Section -->
              <p-accordionpanel value="1">
                <p-accordionheader>
                  <div class="accordion-header">
                    <i class="pi pi-percentage"></i>
                    <span>Configure Discount</span>
                  </div>
                </p-accordionheader>
                <p-accordioncontent>
                  <div class="form-grid" formGroupName="discount">
                    <div class="form-field full-width">
                      <label
                        for="discountType"
                        class="required"
                        [class.has-error]="hasError('discount.discountType')"
                        >Discount Type</label
                      >
                      <p-select
                        id="discountType"
                        formControlName="discountType"
                        [options]="discountTypeOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select type"
                        styleClass="w-full"
                        appendTo="body"
                        [class.ng-invalid]="hasError('discount.discountType')"
                        [class.ng-touched]="hasError('discount.discountType')"
                      />
                      @if (hasError("discount.discountType")) {
                        <small class="p-error">{{
                          getErrorMessage("discount.discountType")
                        }}</small>
                      }
                    </div>

                    <div
                      class="form-field"
                      [class.full-width]="!showMaxPercentageDiscount()"
                    >
                      <label
                        for="amount"
                        class="required"
                        [class.has-error]="hasError('discount.amount')"
                      >
                        {{ discountAmountLabel() }}
                      </label>
                      <p-inputNumber
                        id="amount"
                        formControlName="amount"
                        [min]="0"
                        [max]="isPercentageDiscountType() ? 100 : 999999"
                        [minFractionDigits]="0"
                        [maxFractionDigits]="2"
                        [prefix]="isPercentageDiscountType() ? '' : '$'"
                        [suffix]="isPercentageDiscountType() ? '%' : ''"
                        placeholder="Enter value"
                        inputStyleClass="w-full"
                        [class.ng-invalid]="hasError('discount.amount')"
                        [class.ng-touched]="hasError('discount.amount')"
                      />
                      @if (hasError("discount.amount")) {
                        <small class="p-error">{{
                          getErrorMessage("discount.amount")
                        }}</small>
                      } @else {
                        <small class="field-hint">{{
                          discountAmountHint()
                        }}</small>
                      }
                    </div>

                    @if (showMaxPercentageDiscount()) {
                      <div class="form-field">
                        <label for="maxPercentageDiscount"
                          >Maximum Percentage Discount Amount</label
                        >
                        <p-inputNumber
                          id="maxPercentageDiscount"
                          formControlName="maxPercentageDiscount"
                          [min]="0"
                          prefix="$"
                          placeholder="No limit"
                          inputStyleClass="w-full"
                        />
                        <small class="field-hint"
                          >Enter a maximum amount for the percentage discount.
                          Leave blank for no limit.</small
                        >
                      </div>
                    }

                    @if (showApplyToPriceOption()) {
                      <div class="form-field full-width">
                        <label>Apply To</label>
                        <div class="radio-group">
                          <p-radioButton
                            formControlName="subtotalDiscountAppliesToRegularPrice"
                            [value]="false"
                            inputId="salePrice"
                          />
                          <label for="salePrice" class="radio-label"
                            >Sale Price</label
                          >
                          <p-radioButton
                            formControlName="subtotalDiscountAppliesToRegularPrice"
                            [value]="true"
                            inputId="regularPrice"
                          />
                          <label for="regularPrice" class="radio-label"
                            >Regular Price</label
                          >
                        </div>
                      </div>
                    }

                    <!-- Product/ProductGroup autocomplete for product discount types -->
                    @if (isProductDiscount()) {
                      <div class="form-field full-width">
                        <label for="productSearch">Target Product/Group</label>
                        <p-autoComplete
                          id="productSearch"
                          [(ngModel)]="selectedProductDisplay"
                          [ngModelOptions]="{ standalone: true }"
                          [suggestions]="productSuggestions()"
                          (completeMethod)="productSearch($event)"
                          (onSelect)="productSelected($any($event))"
                          (onClear)="productClear()"
                          field="productAndGroupName"
                          [dropdown]="true"
                          [showClear]="true"
                          placeholder="Search products or groups..."
                          styleClass="w-full"
                          appendTo="body"
                        >
                          <ng-template let-product pTemplate="item">
                            <div class="product-suggestion">
                              <span class="product-name">{{
                                getProductDisplayName(product)
                              }}</span>
                              @if (getProductPrice(product)) {
                                <span class="product-price">
                                  {{ getProductPrice(product) | currency }}
                                  @if (
                                    getProductSalePrice(product) &&
                                    getProductSalePrice(product) !==
                                      getProductPrice(product)
                                  ) {
                                    <span class="sale-price"
                                      >(Sale:
                                      {{
                                        getProductSalePrice(product) | currency
                                      }})</span
                                    >
                                  }
                                </span>
                              }
                              <span
                                class="product-type"
                                [class.type-product]="
                                  getProductType(product) === 'Prod'
                                "
                                [class.type-group]="
                                  getProductType(product) === 'Group'
                                "
                              >
                                {{
                                  getProductType(product) === "Prod"
                                    ? "Product"
                                    : "Product Group"
                                }}
                              </span>
                            </div>
                          </ng-template>
                        </p-autoComplete>
                        <small class="field-hint"
                          >Leave empty to apply to all products</small
                        >
                      </div>

                      <div class="form-field full-width">
                        <div class="toggle-option">
                          <p-inputSwitch
                            [ngModel]="
                              couponForm.controls.discount.controls
                                .productDiscountNotAppliesWhenItHasSalePrice
                                .value
                            "
                            (ngModelChange)="
                              couponForm.controls.discount.controls.productDiscountNotAppliesWhenItHasSalePrice.setValue(
                                $event
                              )
                            "
                            [ngModelOptions]="{ standalone: true }"
                            inputId="doNotApplyToSalePrice"
                          />
                          <label
                            for="doNotApplyToSalePrice"
                            class="toggle-label"
                            >Do not apply to products that have a sale
                            price</label
                          >
                        </div>
                        <small class="field-hint"
                          >When enabled, this discount won't apply when product
                          is already on sale</small
                        >
                      </div>
                    }

                    <!-- Discount Credit/Limits - only for ReduceSubtotalByAmount -->
                    @if (showDiscountLimits()) {
                      <div class="form-field full-width">
                        <label>Discount Limits</label>
                        <div class="radio-group-vertical">
                          <div class="radio-option">
                            <p-radioButton
                              formControlName="discountLimitsMode"
                              value="limited"
                              inputId="limited"
                            />
                            <label for="limited" class="radio-label-block">
                              <span class="radio-title">Limit discount</span>
                              <span class="radio-description"
                                >Discount cannot exceed order total</span
                              >
                            </label>
                          </div>
                          <div class="radio-option">
                            <p-radioButton
                              formControlName="discountLimitsMode"
                              value="unlimited"
                              inputId="unlimited"
                            />
                            <label for="unlimited" class="radio-label-block">
                              <span class="radio-title"
                                >Do not limit discount</span
                              >
                              <span class="radio-description"
                                >Credit carries forward until fully
                                applied</span
                              >
                            </label>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                </p-accordioncontent>
              </p-accordionpanel>

              <!-- Recurring Discounts Section -->
              <p-accordionpanel value="2">
                <p-accordionheader>
                  <div class="accordion-header">
                    <i class="pi pi-sync"></i>
                    <span>Recurring Discounts</span>
                  </div>
                </p-accordionheader>
                <p-accordioncontent>
                  <div class="form-grid" formGroupName="recurring">
                    <div class="form-field full-width">
                      <label>Apply Discount</label>
                      <div class="radio-group vertical">
                        <div class="radio-option">
                          <p-radioButton
                            formControlName="recurringMode"
                            value="forever"
                            inputId="forever"
                          />
                          <label for="forever" class="radio-label"
                            >All future orders</label
                          >
                        </div>
                        <div class="radio-option with-input">
                          <p-radioButton
                            formControlName="recurringMode"
                            value="cycles"
                            inputId="cycles"
                          />
                          <label for="cycles" class="radio-label">Next</label>
                          @if (showCustomCycles()) {
                            <p-inputNumber
                              formControlName="maxCyclesPerScheduledOrder"
                              [min]="1"
                              [max]="100"
                              styleClass="cycles-input"
                              inputStyleClass="cycles-input-field"
                            />
                            <span class="radio-label-suffix">cycles</span>
                          }
                        </div>
                        <div class="radio-option">
                          <p-radioButton
                            formControlName="recurringMode"
                            value="once"
                            inputId="once"
                          />
                          <label for="once" class="radio-label"
                            >One-time only</label
                          >
                        </div>
                      </div>
                    </div>

                    <div class="form-field no-label">
                      <div class="checkbox-option">
                        <p-checkbox
                          formControlName="isStackable"
                          [binary]="true"
                          inputId="isStackable"
                        />
                        <label for="isStackable" class="checkbox-label"
                          >Stackable</label
                        >
                      </div>
                    </div>

                    <div class="form-field">
                      <label for="expirationDate">Expiration</label>
                      <p-datePicker
                        id="expirationDate"
                        formControlName="expirationDate"
                        [showIcon]="true"
                        [showButtonBar]="true"
                        [readonlyInput]="true"
                        placeholder="No expiration"
                        styleClass="w-full"
                        appendTo="body"
                        pTooltip="After the expiration date occurs, the coupon will be invalid only for new scheduled orders. Existing scheduled orders that use an expired coupon won't be affected."
                        tooltipPosition="top"
                      />
                    </div>

                    <div class="form-field full-width">
                      <div class="checkbox-option">
                        <p-checkbox
                          formControlName="applyExpirationDateToExisting"
                          [binary]="true"
                          inputId="applyExpiration"
                        />
                        <label
                          for="applyExpiration"
                          class="checkbox-label"
                          pTooltip="If checked, the expiration date will be applied to existing recurring discounts. If unchecked, it will only apply to new discounts."
                          tooltipPosition="top"
                          >Apply to existing recurring discounts</label
                        >
                      </div>
                    </div>
                  </div>
                </p-accordioncontent>
              </p-accordionpanel>

              <!-- Conversion Limits Section -->
              <p-accordionpanel value="3">
                <p-accordionheader>
                  <div class="accordion-header">
                    <i class="pi pi-users"></i>
                    <span>Conversion Limits</span>
                  </div>
                </p-accordionheader>
                <p-accordioncontent>
                  <div class="form-grid" formGroupName="conversionLimits">
                    <div class="form-field">
                      <label for="maxPerCustomerMode">Max/Customer</label>
                      <p-select
                        id="maxPerCustomerMode"
                        formControlName="maxAssignmentsPerCustomerMode"
                        [options]="maxAssignmentsOptions"
                        placeholder="Select"
                        styleClass="w-full"
                        appendTo="body"
                      />
                    </div>

                    @if (showCustomMaxPerCustomer()) {
                      <div class="form-field">
                        <label for="maxPerCustomer">Custom Limit</label>
                        <p-inputNumber
                          id="maxPerCustomer"
                          formControlName="maxAssignmentsPerCustomer"
                          [min]="1"
                          placeholder="Enter"
                          inputStyleClass="w-full"
                        />
                      </div>
                    }

                    <div class="form-field">
                      <label for="maxPerSite">Max/Site</label>
                      <p-inputNumber
                        id="maxPerSite"
                        formControlName="maxAssignmentsPerSite"
                        [min]="1"
                        placeholder="Unlimited"
                        inputStyleClass="w-full"
                      />
                      <small class="field-hint">Total uses site-wide</small>
                    </div>
                  </div>
                </p-accordioncontent>
              </p-accordionpanel>

              <!-- Advanced Options Section (collapsed by default) -->
              <p-accordionpanel value="4">
                <p-accordionheader>
                  <div class="accordion-header">
                    <i class="pi pi-cog"></i>
                    <span>Advanced Options</span>
                    @if (!advancedOptionsAllowed()) {
                      <span class="tier-badge">
                        <i class="pi pi-lock"></i>
                        {{ currentTierName() || "Upgrade Required" }}
                      </span>
                    }
                  </div>
                </p-accordionheader>
                <p-accordioncontent>
                  @if (!advancedOptionsAllowed()) {
                    <div class="tier-upgrade-notice">
                      <i class="pi pi-info-circle"></i>
                      <span
                        >Advanced options are not available on your current
                        plan.</span
                      >
                      <a
                        href="javascript:void(0)"
                        (click)="navigateToUpgrade()"
                        class="upgrade-link"
                        >Upgrade your plan</a
                      >
                    </div>
                  }
                  <div formGroupName="advancedOptions">
                    <div class="advanced-section">
                      <h4 class="section-title">
                        <i class="pi pi-refresh"></i> Cycles
                      </h4>
                      <div class="form-grid">
                        <div class="form-field">
                          <label for="minCycles">Min</label>
                          <p-inputNumber
                            id="minCycles"
                            formControlName="minScheduledOrderCycles"
                            [min]="0"
                            placeholder="No min"
                            inputStyleClass="w-full"
                            [disabled]="!advancedOptionsAllowed()"
                          />
                        </div>
                        <div class="form-field">
                          <label for="maxCycles">Max</label>
                          <p-inputNumber
                            id="maxCycles"
                            formControlName="maxScheduledOrderCycles"
                            [min]="0"
                            placeholder="No max"
                            inputStyleClass="w-full"
                            [disabled]="!advancedOptionsAllowed()"
                          />
                        </div>
                      </div>
                    </div>

                    <p-divider />

                    <div class="advanced-section">
                      <h4 class="section-title">
                        <i class="pi pi-map-marker"></i> Shipping
                      </h4>
                      <div class="form-grid">
                        <div class="form-field">
                          <label for="country">Country</label>
                          <app-country-select
                            formControlName="country"
                            placeholder="Select Country"
                            appendTo="body"
                            styleClass="w-full"
                            [disabled]="!advancedOptionsAllowed()"
                            (selectionChange)="onCountryChange()"
                          />
                        </div>
                        <div class="form-field">
                          <label for="state">State</label>
                          @if (isUSACountry()) {
                            <app-state-select
                              formControlName="state"
                              placeholder="Select State"
                              appendTo="body"
                              styleClass="w-full"
                              [disabled]="!advancedOptionsAllowed()"
                            />
                          } @else {
                            <input
                              pInputText
                              id="state"
                              formControlName="state"
                              placeholder="Enter state/province"
                              class="w-full"
                            />
                          }
                        </div>
                        <div class="form-field">
                          <label for="city">City</label>
                          <input
                            pInputText
                            id="city"
                            formControlName="city"
                            placeholder="Enter city"
                            class="w-full"
                          />
                        </div>
                        <div class="form-field">
                          <label for="postcode">Postcode</label>
                          <input
                            pInputText
                            id="postcode"
                            formControlName="postcode"
                            placeholder="Enter postcode"
                            class="w-full"
                          />
                        </div>
                      </div>
                    </div>

                    <p-divider />

                    <div class="advanced-section">
                      <h4 class="section-title">
                        <i class="pi pi-box"></i> Order Items
                      </h4>
                      <div class="form-grid">
                        <div class="form-field">
                          <label for="minUnits">Min Units</label>
                          <p-inputNumber
                            id="minUnits"
                            formControlName="minUnits"
                            [min]="0"
                            placeholder="No min"
                            inputStyleClass="w-full"
                            [disabled]="!advancedOptionsAllowed()"
                          />
                        </div>
                        <div class="form-field">
                          <label for="minSubtotal">Min Subtotal</label>
                          <p-inputNumber
                            id="minSubtotal"
                            formControlName="minSubtotal"
                            [min]="0"
                            prefix="$"
                            placeholder="No min"
                            inputStyleClass="w-full"
                            [disabled]="!advancedOptionsAllowed()"
                          />
                        </div>
                      </div>
                      <!-- Weight fields in 3-column layout -->
                      <div class="weight-grid">
                        <div class="form-field">
                          <label for="minWeight">Min Weight</label>
                          <p-inputNumber
                            id="minWeight"
                            formControlName="minWeight"
                            [min]="0"
                            placeholder="No min"
                            inputStyleClass="w-full"
                            [disabled]="!advancedOptionsAllowed()"
                          />
                        </div>
                        <div class="form-field">
                          <label for="maxWeight">Max Weight</label>
                          <p-inputNumber
                            id="maxWeight"
                            formControlName="maxWeight"
                            [min]="0"
                            placeholder="No max"
                            inputStyleClass="w-full"
                            [disabled]="!advancedOptionsAllowed()"
                          />
                        </div>
                        <div class="form-field">
                          <label for="weightUnit">Unit</label>
                          <p-select
                            id="weightUnit"
                            formControlName="weightUnitType"
                            [options]="weightUnitOptions"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select"
                            styleClass="w-full"
                            appendTo="body"
                            [disabled]="!advancedOptionsAllowed()"
                          />
                        </div>
                      </div>
                    </div>

                    <p-divider />

                    <div class="advanced-section">
                      <h4 class="section-title">
                        <i class="pi pi-user"></i> Targeting
                      </h4>
                      <div class="form-grid">
                        <div class="form-field full-width">
                          <div class="radio-group vertical">
                            <div class="radio-option">
                              <p-radioButton
                                formControlName="customerMode"
                                value="all"
                                inputId="allCustomers"
                                [disabled]="!advancedOptionsAllowed()"
                              />
                              <label for="allCustomers" class="radio-label"
                                >All customers</label
                              >
                            </div>
                            <div class="radio-option">
                              <p-radioButton
                                formControlName="customerMode"
                                value="include"
                                inputId="includeCustomers"
                                [disabled]="!advancedOptionsAllowed()"
                              />
                              <label for="includeCustomers" class="radio-label"
                                >Include emails</label
                              >
                            </div>
                            <div class="radio-option">
                              <p-radioButton
                                formControlName="customerMode"
                                value="exclude"
                                inputId="excludeCustomers"
                                [disabled]="!advancedOptionsAllowed()"
                              />
                              <label for="excludeCustomers" class="radio-label"
                                >Exclude emails</label
                              >
                            </div>
                          </div>
                        </div>

                        @if (
                          couponForm.controls.advancedOptions.controls
                            .customerMode.value === "include"
                        ) {
                          <div class="form-field full-width">
                            <label for="includeEmails">Include Emails</label>
                            <input
                              pInputText
                              id="includeEmails"
                              formControlName="includeCustomerEmails"
                              placeholder="Comma-separated"
                              class="w-full"
                              [disabled]="!advancedOptionsAllowed()"
                            />
                          </div>
                        }

                        @if (
                          couponForm.controls.advancedOptions.controls
                            .customerMode.value === "exclude"
                        ) {
                          <div class="form-field full-width">
                            <label for="excludeEmails">Exclude Emails</label>
                            <input
                              pInputText
                              id="excludeEmails"
                              formControlName="excludeCustomerEmails"
                              placeholder="Comma-separated"
                              class="w-full"
                              [disabled]="!advancedOptionsAllowed()"
                            />
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </p-accordioncontent>
              </p-accordionpanel>
            </p-accordion>
          </div>
        </div>

        <!-- Right Column: Summary Card (Desktop Only) -->
        <div class="summary-column">
          <div class="summary-card-wrapper">
            <p-card styleClass="summary-card">
              <ng-template pTemplate="header">
                <div class="summary-header">
                  <i class="pi pi-info-circle"></i>
                  <span>Coupon Summary</span>
                </div>
              </ng-template>
              <ng-container *ngTemplateOutlet="summaryContent" />
            </p-card>
          </div>
        </div>
      </div>
    </form>
  }
</div>

<!-- Summary Content Template -->
<ng-template #summaryContent>
  <div class="summary-content">
    <div class="summary-row">
      <span class="summary-label">Code</span>
      <span class="summary-value code-value">{{
        couponForm.controls.details.controls.code.value || "—"
      }}</span>
    </div>

    <div class="summary-row">
      <span class="summary-label">Discount</span>
      <span class="summary-value">
        @if (couponForm.controls.discount.controls.amount.value !== null) {
          @if (isPercentageDiscountType()) {
            {{ couponForm.controls.discount.controls.amount.value }}%
          } @else {
            ${{
              couponForm.controls.discount.controls.amount.value
                | number: "1.2-2"
            }}
          }
        } @else {
          —
        }
      </span>
    </div>

    <div class="summary-row">
      <span class="summary-label">Type</span>
      <span class="summary-value type-value">{{
        getDiscountTypeLabel(
          couponForm.controls.discount.controls.discountType.value
        )
      }}</span>
    </div>

    <p-divider />

    <div class="summary-row">
      <span class="summary-label">Recurring</span>
      <span class="summary-value">
        @switch (couponForm.controls.recurring.controls.recurringMode.value) {
          @case ("forever") {
            <span class="recurring-badge forever">Forever</span>
          }
          @case ("cycles") {
            <span class="recurring-badge cycles"
              >{{
                couponForm.controls.recurring.controls
                  .maxCyclesPerScheduledOrder.value || "?"
              }}
              cycles</span
            >
          }
          @case ("once") {
            <span class="recurring-badge once">One-time</span>
          }
        }
      </span>
    </div>

    <div class="summary-row">
      <span class="summary-label">Stackable</span>
      <span class="summary-value">
        @if (couponForm.controls.recurring.controls.isStackable.value) {
          <i class="pi pi-check-circle stackable-yes"></i> Yes
        } @else {
          <i class="pi pi-times-circle stackable-no"></i> No
        }
      </span>
    </div>

    <p-divider />

    <div class="summary-row">
      <span class="summary-label">Uses/Customer</span>
      <span class="summary-value">
        @if (
          couponForm.controls.conversionLimits.controls
            .maxAssignmentsPerCustomerMode.value === "Unlimited"
        ) {
          Unlimited
        } @else if (
          couponForm.controls.conversionLimits.controls
            .maxAssignmentsPerCustomerMode.value === "Custom"
        ) {
          {{
            couponForm.controls.conversionLimits.controls
              .maxAssignmentsPerCustomer.value || "—"
          }}
        } @else {
          {{
            couponForm.controls.conversionLimits.controls
              .maxAssignmentsPerCustomerMode.value
          }}
        }
      </span>
    </div>

    <div class="summary-row">
      <span class="summary-label">Uses/Site</span>
      <span class="summary-value">{{
        couponForm.controls.conversionLimits.controls.maxAssignmentsPerSite
          .value || "Unlimited"
      }}</span>
    </div>

    <p-divider />

    <div class="summary-row">
      <span class="summary-label">Expires</span>
      <span class="summary-value">
        @if (couponForm.controls.recurring.controls.expirationDate.value) {
          {{
            couponForm.controls.recurring.controls.expirationDate.value
              | date: "mediumDate"
          }}
        } @else {
          <span class="no-expiration">Never</span>
        }
      </span>
    </div>

    @if (
      showMaxPercentageDiscount() &&
      couponForm.controls.discount.controls.maxPercentageDiscount.value
    ) {
      <div class="summary-row">
        <span class="summary-label">Max % Cap</span>
        <span class="summary-value"
          >${{
            couponForm.controls.discount.controls.maxPercentageDiscount.value
              | number: "1.2-2"
          }}</span
        >
      </div>
    }
  </div>
</ng-template>
